import { getEnvVar } from "@/utils/util";
import { publicAxios } from "./axiosInstance";
import { getAccessToken } from "@/utils/auth";
import type { PublicProgramExploreOut, PublicProgramPreviewOut } from "./api";

const API_BASE = getEnvVar("API_BASE");
// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  addAccountFlow?: boolean;
  /**
   * external_id of an Expert Program this user still has an OPEN (INVITED)
   * achiever invitation to. Server-resolved from program_invitations by
   * email, so it survives a browser/device change that wipes localStorage.
   * Absent/null when nothing is pending.
   */
  pendingProgramExternalId?: string | null;
}

export interface VerifyEmailResponse {
  detail: string;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
}

/**
 * Advisor acceptance response type
 */
export interface AdvisorAcceptanceResponse {
  success: boolean;
  message: string;
}

export interface AdvisoryStyleItem {
  title: string;
  description: string;
}

/** A locked STAR card -- the API only ever sends the title and a short preview. */
export interface PublicStarPreview {
  title: string;
  preview?: string | null;
}

export interface PublicProfileProgram {
  externalId: string;
  name: string;
  description?: string | null;
  /** null when the expert hasn't priced it yet -- distinct from 0 (free). */
  priceCents?: number | null;
  /** The plan's billing interval ("year"), or null for a one-time price. */
  priceInterval?: string | null;
}

export interface PublicAdvisorProfileResponse {
  fullName: string;
  publicSlug: string;
  currentTitle?: string;
  currentCompany?: string;
  location?: string;
  yearsOfExperience?: string;
  primaryFunction?: string;
  currentRoleLevel?: string;
  experienceContext?: string[];
  bio?: string;
  linkedIn?: string;
  personalWebsite?: string;
  profilePictureUrl?: string | null;
  levelsMentored?: string[];
  coachingStrengths?: string[];
  advisoryStyles?: AdvisoryStyleItem[];
  operatorThesis?: string | null;
  problemAndInsight?: string | null;
  advisoryPhilosophy?: string | null;
  coreIndustries?: string[];
  organisationTypes?: string[];
  starPortfolio?: PublicStarPreview[];
  /** Only on the single-profile endpoint, not the list. */
  programs?: PublicProfileProgram[];
}

// Public API methods
export const publicApi = {
  profiles: {
    getAdvisorPublicProfile: async (slug: string): Promise<PublicAdvisorProfileResponse> => {
      const response = await publicAxios.get(
        `${API_BASE}/public/advisors/profile/${encodeURIComponent(slug)}`,
      );
      return response.data;
    },
    listPublicAdvisors: async (): Promise<PublicAdvisorProfileResponse[]> => {
      const response = await publicAxios.get(`${API_BASE}/public/advisors`);
      return response.data;
    },
  },
  // Auth methods
  auth: {
    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
      const response = await publicAxios.post(`${API_BASE}/auth/login`, credentials);
      return response.data;
    },
    resendEmail: async (email: string): Promise<void> => {
      const response = await publicAxios.post(`${API_BASE}/auth/login/resend`, { email });
      return response.data;
    },
    signup: async (userData: {
      email: string;
      fullName: string;
      role: string;
      mobileNumber: string;
      organizationCode: string;
      connectToUserId?: string;
      expertProgramExternalId?: string;
      expertProgramUrlRole?: string;
      createExpertProgram?: boolean;
      /** The ?ref= from a referral link — the referrer's user id. Ignored
       *  server-side if it names nobody, so a stale link never blocks signup. */
      referredByUserId?: string;
    }): Promise<{ detail: string; sessionToken: string }> => {
      const response = await publicAxios.post(`${API_BASE}/auth/signup`, userData);
      return response.data;
    },
    verifyEmail: async (token: string): Promise<AuthResponse> => {
      const response = await publicAxios.get(
        `${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`,
      );
      return response.data;
    },
    /**
     * Accept advisor invitation with token
     * POST /api/v1/auth/advisor-accept
     * @param token - The invitation token
     * @returns {Promise<AdvisorAcceptanceResponse>}
     */
    advisorAccept: async (token: string): Promise<AdvisorAcceptanceResponse> => {
      const response = await publicAxios.get(`${API_BASE}/auth/advisor-accept?token=${token}`);
      return response.data;
    },
    resendVerificationEmail: async (token: string): Promise<{ detail: string; userId: string }> => {
      const response = await publicAxios.post(`${API_BASE}/auth/resend-verification-email`, {
        token: token,
      });
      return response.data;
    },
    isVerified: async (userId: string): Promise<{ userId: string; isVerified: boolean }> => {
      const response = await publicAxios.get(`${API_BASE}/auth/is-verified?userId=${userId}`);
      return response.data;
    },
    sendLoginLink: async (
      email: string,
      connectToUserId?: string,
      expectedTargetUserId?: string,
    ): Promise<{ detail: string }> => {
      const body: Record<string, string> = { email };
      if (connectToUserId) body.connectToUserId = connectToUserId;
      if (expectedTargetUserId) body.expectedTargetUserId = expectedTargetUserId;
      const response = await publicAxios.post(`${API_BASE}/auth/login`, body);
      return response.data;
    },
    verifyLoginToken: async (
      token: string,
    ): Promise<{
      accessToken: string;
      tokenType: string;
      addAccountFlow?: boolean;
      pendingProgramExternalId?: string | null;
    }> => {
      const response = await publicAxios.post(`${API_BASE}/auth/login/verify`, { token });
      return response.data;
    },
    /**
     * Password login — accepted by the backend ONLY for the configured review
     * account. Every other email is refused (403) and must use the magic-link
     * flow. Returns the same shape as verifyLoginToken on success.
     * POST /api/v1/auth/login/password
     */
    passwordLogin: async (
      email: string,
      password: string,
    ): Promise<{
      accessToken: string;
      tokenType: string;
      addAccountFlow?: boolean;
      pendingProgramExternalId?: string | null;
    }> => {
      const response = await publicAxios.post(`${API_BASE}/auth/login/password`, {
        email,
        password,
      });
      return response.data;
    },
  },
  programs: {
    /**
     * A program's public landing payload, for either program type.
     *
     * Anonymous-safe, but attaches the token when one exists: with a session the
     * response carries `isEnrolled`, which is what decides between the Enroll
     * button and "Already Subscribed". Sent unauthenticated it always reads
     * false, so an enrolled achiever would still be offered Enroll.
     *
     * A PEER_ADVISORY program 404s for a viewer who is not an approved Expert --
     * the same rule that hides it from the Explore list. So a 404 here means
     * "not found OR not for you", deliberately indistinguishable, and callers
     * should render their ordinary not-found state rather than a locked page.
     */
    getPreview: async (programId: string): Promise<PublicProgramPreviewOut> => {
      const token = getAccessToken();
      const response = await publicAxios.get(`${API_BASE}/public/programs/${programId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return response.data;
    },
    /**
     * Explore page cards, pre-split into `featured` (carousel, never
     * paginated) and `programs` (grid, paginated via page/pageSize).
     *
     * Featured programs appear in BOTH lists -- `featured` is the carousel's
     * copy, not a slice removed from the grid.
     *
     * Stays reachable with no session (anonymous browsing), but attaches the
     * current token when one exists. Programs the caller runs or is enrolled in
     * stay in the list and are marked (`viewerRole`, `isEnrolled`) rather than
     * removed -- that is what tells an enrolled card apart from a joinable one.
     *
     * THE RESPONSE DEPENDS ON WHO IS ASKING.
     * --------------------------------------
     * Expert Programs go to everyone, as they always have. Peer Advisory
     * programs are returned only to a viewer whose Expert Application is
     * APPROVED, and are absent entirely otherwise -- including from
     * `totalPrograms`, so the page controls stay honest.
     *
     * So ANY cache keyed on page/pageSize alone will serve one identity's list
     * to another after a login or logout. The caller's query key must include
     * the viewer -- see ExploreProgramsSection.
     */
    listExplore: async (page = 1, pageSize = 10): Promise<PublicProgramExploreOut> => {
      const token = getAccessToken();
      const response = await publicAxios.get(`${API_BASE}/public/programs`, {
        params: { page, page_size: pageSize },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return response.data;
    },
  },
};
